import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiDollarSign, FiClock, FiTruck, FiStar, FiInfo } = FiIcons;

const Pricing = () => {
  const [selectedTab, setSelectedTab] = useState('wash-fold');

  const washFoldPricing = [
    {
      category: 'Regular Items',
      items: [
        { name: 'T-shirts, Polo Shirts', price: '$1.50 per lb' },
        { name: 'Jeans, Casual Pants', price: '$1.50 per lb' },
        { name: 'Shorts, Skirts', price: '$1.50 per lb' },
        { name: 'Underwear, Socks', price: '$1.50 per lb' },
        { name: 'Pajamas, Loungewear', price: '$1.50 per lb' }
      ]
    },
    {
      category: 'Delicate Items',
      items: [
        { name: 'Dress Shirts', price: '$2.99 each' },
        { name: 'Blouses', price: '$3.99 each' },
        { name: 'Sweaters', price: '$4.99 each' },
        { name: 'Delicate Dresses', price: '$6.99 each' }
      ]
    }
  ];

  const dryCleaningPricing = [
    {
      category: 'Men\'s Clothing',
      items: [
        { name: 'Suits (2-piece)', price: '$18.99' },
        { name: 'Suits (3-piece)', price: '$24.99' },
        { name: 'Dress Shirts', price: '$4.99' },
        { name: 'Pants/Trousers', price: '$8.99' },
        { name: 'Sport Coats', price: '$12.99' },
        { name: 'Ties', price: '$3.99' }
      ]
    },
    {
      category: 'Women\'s Clothing',
      items: [
        { name: 'Dresses (Simple)', price: '$12.99' },
        { name: 'Dresses (Formal)', price: '$18.99' },
        { name: 'Blouses', price: '$6.99' },
        { name: 'Skirts', price: '$8.99' },
        { name: 'Pants/Slacks', price: '$8.99' },
        { name: 'Blazers', price: '$12.99' }
      ]
    },
    {
      category: 'Specialty Items',
      items: [
        { name: 'Wedding Dresses', price: '$89.99+' },
        { name: 'Leather Jackets', price: '$29.99' },
        { name: 'Suede Items', price: '$24.99' },
        { name: 'Fur Coats', price: '$49.99+' },
        { name: 'Comforters (Twin)', price: '$25.99' },
        { name: 'Comforters (Queen/King)', price: '$35.99' }
      ]
    }
  ];

  const alterationsPricing = [
    {
      category: 'Basic Alterations',
      items: [
        { name: 'Hem Pants (No Cuff)', price: '$12.99' },
        { name: 'Hem Pants (With Cuff)', price: '$15.99' },
        { name: 'Hem Dress/Skirt', price: '$14.99' },
        { name: 'Take in/Let out Waist', price: '$18.99' },
        { name: 'Shorten Sleeves', price: '$16.99' }
      ]
    },
    {
      category: 'Advanced Alterations',
      items: [
        { name: 'Zipper Replacement', price: '$24.99' },
        { name: 'Button Replacement', price: '$3.99 each' },
        { name: 'Repair Small Tears', price: '$8.99' },
        { name: 'Replace Lining', price: '$39.99' },
        { name: 'Custom Fitting', price: '$49.99+' }
      ]
    }
  ];

  const packages = [
    {
      name: 'Basic Package',
      price: '$29.99',
      period: 'per month',
      description: 'Perfect for individuals or small households',
      features: [
        'Up to 20 lbs of wash & fold',
        'Free pickup & delivery',
        'Standard turnaround (48 hours)',
        'Basic stain treatment',
        'Folded and packaged'
      ],
      popular: false
    },
    {
      name: 'Family Package',
      price: '$49.99',
      period: 'per month',
      description: 'Ideal for busy families',
      features: [
        'Up to 40 lbs of wash & fold',
        'Free pickup & delivery',
        'Priority turnaround (24 hours)',
        'Advanced stain treatment',
        'Folded and packaged',
        '2 dry cleaning items included'
      ],
      popular: true
    },
    {
      name: 'Premium Package',
      price: '$79.99',
      period: 'per month',
      description: 'Complete laundry solution',
      features: [
        'Unlimited wash & fold',
        'Free pickup & delivery',
        'Same-day service available',
        'Premium stain treatment',
        'Folded and packaged',
        '5 dry cleaning items included',
        'Minor alterations included'
      ],
      popular: false
    }
  ];

  const tabs = [
    { id: 'wash-fold', name: 'Wash & Fold', icon: FiDollarSign },
    { id: 'dry-cleaning', name: 'Dry Cleaning', icon: FiStar },
    { id: 'alterations', name: 'Alterations', icon: FiCheck },
    { id: 'packages', name: 'Monthly Packages', icon: FiTruck }
  ];

  const renderPricingTable = (pricingData) => (
    <div className="space-y-8">
      {pricingData.map((category, categoryIndex) => (
        <div key={categoryIndex} className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-600 text-white px-6 py-4">
            <h3 className="text-xl font-semibold">{category.category}</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {category.items.map((item, itemIndex) => (
                <div key={itemIndex} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-gray-700">{item.name}</span>
                  <span className="font-semibold text-blue-600">{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPackages = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {packages.map((pkg, index) => (
        <div key={index} className={`bg-white rounded-lg shadow-lg overflow-hidden ${pkg.popular ? 'ring-2 ring-blue-500 transform scale-105' : ''}`}>
          {pkg.popular && (
            <div className="bg-blue-500 text-white text-center py-2 text-sm font-semibold">
              Most Popular
            </div>
          )}
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
            <p className="text-gray-600 mb-6">{pkg.description}</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-blue-600">{pkg.price}</span>
              <span className="text-gray-500">/{pkg.period}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {pkg.features.map((feature, featureIndex) => (
                <li key={featureIndex} className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 text-center block ${
                pkg.popular 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Choose Package
            </Link>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Pricing & Packages
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transparent, competitive pricing for all your laundry and dry cleaning needs. 
            No hidden fees, just quality service at fair prices.
          </p>
        </div>
      </div>

      {/* Pricing Navigation */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                  selectedTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5" />
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Content */}
      <div className="container mx-auto px-4 pb-16">
        {selectedTab === 'wash-fold' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Wash & Fold Pricing</h2>
              <p className="text-lg text-gray-600">Professional washing, drying, and folding services</p>
            </div>
            {renderPricingTable(washFoldPricing)}
          </div>
        )}

        {selectedTab === 'dry-cleaning' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Dry Cleaning Pricing</h2>
              <p className="text-lg text-gray-600">Expert care for your delicate and special garments</p>
            </div>
            {renderPricingTable(dryCleaningPricing)}
          </div>
        )}

        {selectedTab === 'alterations' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Alterations & Repairs</h2>
              <p className="text-lg text-gray-600">Professional tailoring and garment repair services</p>
            </div>
            {renderPricingTable(alterationsPricing)}
          </div>
        )}

        {selectedTab === 'packages' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Monthly Packages</h2>
              <p className="text-lg text-gray-600">Save money with our convenient monthly subscription packages</p>
            </div>
            {renderPackages()}
          </div>
        )}
      </div>

      {/* Additional Information */}
      <div className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <SafeIcon icon={FiTruck} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Free Pickup & Delivery</h3>
              <p>Within 5 miles for orders over $25</p>
            </div>
            <div>
              <SafeIcon icon={FiClock} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Quick Turnaround</h3>
              <p>Most items ready within 24-48 hours</p>
            </div>
            <div>
              <SafeIcon icon={FiCheck} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Satisfaction Guarantee</h3>
              <p>100% satisfaction or money back</p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start space-x-3 mb-6">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-yellow-600 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Important Pricing Information</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Minimum order of $15 for pickup and delivery service</li>
                  <li>• Prices may vary for heavily soiled or stained items requiring special treatment</li>
                  <li>• Rush service (same day) available for additional 50% charge</li>
                  <li>• Wedding dress cleaning and preservation quoted separately</li>
                  <li>• Commercial accounts receive volume discounts - contact us for custom pricing</li>
                  <li>• All prices subject to change without notice</li>
                </ul>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Questions About Pricing?</h3>
              <p className="text-gray-600 mb-6">
                Contact us for a custom quote or if you have questions about our pricing structure.
              </p>
              <Link
                to="/contact"
                className="bg-blue-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Get Custom Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;